import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import React from 'react';
import { NAVY, RADIUS_CARD, SHADOW, YELLOW } from 'styles/tokens';

const BLUE = '#2C5889';
const TEAL = '#25616F';

const FUNNEL_STAGES = [
  {
    label: 'Propuesta creada',
    statuses: ['PROPOSAL_CREATED', 'PENDING_EMISSION', 'EMITTED'],
    color: BLUE,
    textColor: '#ffffff',
  },
  {
    label: 'Pendiente de emisión',
    statuses: ['PENDING_EMISSION', 'EMITTED'],
    color: TEAL,
    textColor: '#ffffff',
  },
  {
    label: 'Emitida',
    statuses: ['EMITTED'],
    color: YELLOW,
    textColor: NAVY,
  },
];

interface Props {
  policiesByStatus: Record<string, number>;
}

const PolicyFunnel: React.FC<Props> = ({ policiesByStatus }) => {
  const stages = FUNNEL_STAGES.map((stage) => ({
    ...stage,
    value: stage.statuses.reduce((sum, s) => sum + (policiesByStatus[s] || 0), 0),
  }));

  const maxValue = stages[0]?.value || 1;

  if (maxValue === 0) return null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, py: 1 }}>
      {stages.map((stage, idx) => {
        const widthPct = Math.max((stage.value / maxValue) * 100, 15);
        const prevValue = idx > 0 ? stages[idx - 1].value : null;
        const conversionPct = prevValue && prevValue > 0 ? Math.round((stage.value / prevValue) * 100) : null;

        return (
          <React.Fragment key={stage.label}>
            {idx > 0 && conversionPct !== null && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 0.25 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  ↓ {conversionPct}% conversión
                </Typography>
              </Box>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Box
                sx={{
                  width: `${widthPct}%`,
                  backgroundColor: stage.color,
                  borderRadius: RADIUS_CARD,
                  py: 1.5,
                  px: 2,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  minWidth: 200,
                  boxShadow: SHADOW,
                  transition: 'all 0.3s ease',
                  animation: `funnelSlideIn 0.5s cubic-bezier(0.4, 0, 0.2, 1) ${idx * 0.15}s both`,
                  '@keyframes funnelSlideIn': {
                    '0%': { opacity: 0, transform: 'translateX(-24px)' },
                    '100%': { opacity: 1, transform: 'translateX(0)' },
                  },
                  '&:hover': {
                    opacity: 0.92,
                    transform: 'scale(1.01)',
                  },
                }}
              >
                <Typography variant="body2" noWrap sx={{ fontWeight: 600, color: stage.textColor }}>
                  {stage.label}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, color: stage.textColor }}>
                  {stage.value}
                </Typography>
              </Box>
            </Box>
          </React.Fragment>
        );
      })}
    </Box>
  );
};

export default PolicyFunnel;
